import type { ModuleWithProviders } from '@angular/core';
import {
  RouterModule,
  Routes
} from '@angular/router';
import { USERROLES } from './app.config';

import { MsalGuard } from '@azure/msal-angular';
import { BrowserUtils } from '@azure/msal-browser';
import { LogoutComponent } from '../app/core/auth/logout/logout.component';
import { UserSettingsResolverService } from '../app/core/auth/resolver/user-settings.resolver';
import { AHNavigationResolverService } from './asset-harvester/resolvers/ah-navigation-resolver.service';
import { CleanupComponent } from './aws-az-redirections/cleanup/cleanup.component';
import { AuthenticationGuard } from './core/auth/guards/authentication-guard';
import { AuthorizationGuard } from './core/auth/guards/authorization-guard';
import { LoginComponent } from './core/auth/login/login.component';
import { ApplicationDowntimeComponent } from './core/components/application-downtime/application-downtime.component';
import { ConsentPageComponent } from './core/components/consent-page/consent-page.component';
import { HomeComponent } from './core/components/home/home.component';
import { ErrorPageComponent } from './core/fallback-modules/error-page/error-page.component';
import { feedflexRedirectMatcher } from './crude-flex/feedFlexGuardMatcher/feedFlex-redirect-matcher';
import { FeedFlexRedirectGuard } from './crude-flex/feedFlexGuardMatcher/feedFlex-redirect.guard';
import { FsqsRedirectGuard } from './fsqs/route_guard/fsqs_redirect_guard';
import { fsqsRedirectMatcher } from './fsqs/route_guard/fsqs_redirect_matcher';
import { paperRedirectMatcher } from './paper/paperGuardMatcher/paper-redirect-matcher';
import { PaperRedirectGuard } from './paper/paperGuardMatcher/paper-redirect.guard';
import { qualityRedirectMatcher } from './quality/qualityGuardMatcher/quality-redirect-matcher';
import { QualityRedirectGuard } from './quality/qualityGuardMatcher/quality-redirect.guard';
import { ChildRouteRedirectComponent } from './shared-ui-refresh/child-route-redirect-component/child-route-redirect.component';
import { ServiceType } from './shared/_enum/constants';
import { FnbComponent } from './shared/components/fnb/fnb.component';
import { wfiRedirectMatcher } from './water-flow-intelligence/wfiGuardMatcher/wfi-redirect-matcher';
import { wfiRedirectGuard } from './water-flow-intelligence/wfiGuardMatcher/wfi-redirect.guard';
import { wsiRedirectMatcher } from './water-safety/wsiGuardMatcher/wsi-redirect-matcher';
import { wsiRedirectGuard } from './water-safety/wsiGuardMatcher/wsi-redirect.guard';
import { SelectivePreloadingStrategy } from './core/routing/selective-preloading.strategy';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const ADRUM: unknown;

// BASE APP ROUTE
const APP_ROUTES: Routes = <Routes>[
  {
    path: 'cleanup',
    component: CleanupComponent,
    pathMatch: 'full',
  },
  // for default redirect never put prefix for pathMatch
  // it should be always full, so that it will apply for once
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [MsalGuard, AuthenticationGuard],
    pathMatch: 'full',
    data: {
      hideSubHeader: true,
    },
  },

  {
    path: 'logout',
    component: LogoutComponent,
    data: {
      hideLayout: true,
      globalIdleEnabled: false,
    },
    pathMatch: 'full',
  },
  // { path: 'assettree', redirectTo: 'asset-tree', pathMatch: 'full' },

  {
    path: 'login',
    component: LoginComponent,
    resolve: { UserSettings: UserSettingsResolverService },
    canActivate: [MsalGuard]
  },
  {
    // for MSAL gettting the token
    path: 'code',
    component: LoginComponent,
    canActivate: [MsalGuard]
  },
  {
    path: 'asset-tree',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./asset-tree/asset-tree.module').then(m => m.AssetTreeModule),
    data: {
      // this will allow all the users who have access to just AH see the Asset Tree also
      // security needs to be implemented in AT screen to restrict
      origin: 'at',
      roles: [
        USERROLES.ASSETTREEADMIN,
        USERROLES.ASSETTREEVIEW,
        USERROLES.ASSETHARVESTERREAD,
        USERROLES.ASSETHARVESTERREADWRITE,
      ],
      globalIdleEnabled: false,
    },
  },
  {
    path: 'assetharvester',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./asset-tree/asset-tree.module').then(m => m.AssetTreeModule),
    data: {
      // this will allow all the users who have access to just AH see the Asset Tree also
      // security needs to be implemented in AT screen to restrict
      preload: true,
      origin: 'ah',
      roles: [
        USERROLES.ASSETTREEADMIN,
        USERROLES.ASSETTREEVIEW,
        USERROLES.ASSETHARVESTERREAD,
        USERROLES.ASSETHARVESTERREADWRITE,
      ],
      globalIdleEnabled: false,
    },
    resolve: { 'AHNavigation': AHNavigationResolverService }
  },
  {
    path: 'assetharvester-manage-sites',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./AH-manage-sites/AH-manage-sites.module').then(m => m.AHManageSitesModule),

    data: {
      hideSubHeader: false,
      roles: [
      ]
    },
  },
  {
    path: 'contextDiagram/:moduleName/:mapType/:serviceType/:siteId',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadComponent: () => import('./shared/e3d-modules/contextdiagram-map-configuration/components/contextdiagram-layout/contextdiagram-layout.component').then(m => m.ContextDiagramLayoutComponent),
    data: {
      hideSubHeader: false,
      roles: [
      ]
    },
  },
  {
    path: 'credential-management',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./credential-management/credential-management.module').then(m => m.CredentialManagementModule),
    data: {
      breadcrumb: 'Device Credential Management',
      roles: [USERROLES.CREDMGMT],
      globalIdleEnabled: false
    }
  },
  {
    path: 'certificate-generate',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./certificate-generate/certificate-generate.module').then(m => m.CertificateGenerateModule),
    data: {
      breadcrumb: 'Device Certificate Generator',
      roles: [USERROLES.CERTGEN_DEVICE_CREATE],
      globalIdleEnabled: false
    }
  },
  {
    path: 'manual-equipment-creation',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./manual-equipment-creation/manual-equipment-creation.module').then(m => m.ManualEquipmentCreationModule),
    data: {
      breadcrumb: 'Manual Asset Creation',
      roles:[USERROLES.HARVESTERMANUALEQUIPMENTAPPROVER, USERROLES.HARVESTERMANUALEQUIPMENTREQUESTOR],
      globalIdleEnabled: false
    }
  },
  {
    path: 'offline-data-upload',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./offline-data-upload/offline-data-upload.module').then(m => m.OfflineDataUploadModule),
    data: {
      breadcrumb: 'Offline Data Upload',
      roles: [USERROLES.ENVISION4WEB], //Need to check,
      path: 'offline-data-upload',
    },
  },
  {
    path: 'controller-management',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import(
      './controller-management/controller-management.module').then(m => m.ControllerManagementModule),
    data: {
      breadcrumb: 'Controller Management',
      roles: [USERROLES.ASSETHARVESTERREADWRITE, USERROLES.ENVISIONSALES], //Need to check,
      path: 'controller-management',
    },
  },
  {
    path: 'shared-ui-components',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import(
      './shared-ui-components/ui-components.module').then(m => m.TestSharedModule)
  },
  {
    path: 'recycle-intelligence-configuration',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./recycle-intelligence-configuration/recycle-intelligence-configuration.module').then(m => m.RecycleIntelligenceConfigurationModule),
    data: {
      breadcrumb: "Recycle Intelligence Configuration",
      path: 'recycle-intelligence-configuration',
      roles: [USERROLES.RECYCLE_INTELLIGENCE_ADMIN]
    }
  },
  {
    path: 'logo-management',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./service-summary/admin/service-summary-admin.module').then(m => m.ServiceSummaryAdminModule),
    data: {
      breadcrumb: 'Logo Management',
      path: 'logo-management',
      roles: [USERROLES.SSD_LOGO_MANAGEMENT],
    },
  },
  {
    path: 'assetharvester',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./asset-harvester/asset-harvester.module').then(m => m.AssetHarvesterModule),
    data: {
      preload: true,
      roles: [USERROLES.ASSETHARVESTERREAD, USERROLES.ASSETHARVESTERREADWRITE],
      globalIdleEnabled: false,
    },
    resolve: { 'AHNavigation': AHNavigationResolverService }
  },
  /******** Water Safety Route starts***********/
  {
    matcher: wsiRedirectMatcher,
    canActivate: [wsiRedirectGuard],
    component: ChildRouteRedirectComponent
  },
  {
    path: 'water-safety-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./water-safety/water-safety.module').then(m => m.WaterSafetyModule),
    data: {
      breadcrumb: 'Ecolab® Water Safety IQ™ - Enterprise Overview',
      roles: [USERROLES.WSIDASHBOARD],
      serviceTypeId: ServiceType.WaterSafety
    },
  },
  {
    path: 'water-safety',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./water-safety/water-safety.module').then(m => m.WaterSafetyModule),
    data: {
      breadcrumb: 'Water Safety Intelligence - Enterprise Overview',
      roles: [USERROLES.WSIDASHBOARD],
      serviceTypeId: ServiceType.WaterSafety
    },
  },
  {
    path: 'legionella-test-results',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('../libraries/e3dindustrial-watersafetyaws-mfe/libs/water-safety-aws/src/public-api').then(m => m.WaterSafetyAwsModule),
    data: {
      breadcrumb: 'Legionella Test Results',
      roles: [USERROLES.LEGIONELLA_TEST_RESULTS_DASHBOARD],
      hideSubHeader: true,
      serviceTypeId: ServiceType.WaterSafety
    },
  },
  {
    path: 'upload-reports-and-plans',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadComponent: () => import('../libraries/e3dindustrial-watersafetyaws-mfe/libs/water-safety-aws/src/lib/components/report-upload/report-upload.component').then(m => m.ReportUploadComponent),
    data: {
      breadcrumb: 'Upload Reports and Plans',
      roles: [USERROLES.LEGIONELLA_DASHBOARD_SAMPLE_REPORT_UPLOAD, USERROLES.LEGIONELLA_DASHBOARD_WM_PLAN_UPLOAD],
      hideSubHeader: true,
      serviceTypeId: ServiceType.WaterSafety
    },
  },
  //   {
  //   path: 'water-safety-iq/TaskManager',
  //   canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
  //   canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
  //   loadChildren: () => import('./wsi-task-manager/wsi-task-manager.module').then(m => m.WsiTaskManagerModule),
  //   data: {
  //     breadcrumb: 'Enterprise Overview',
  //     roles: [USERROLES.WSAFTYIQTM],
  //     serviceTypeId: ServiceType.WaterManagementPlan,
  //     hideSubHeader: true,
  //   },
  // },
  // {
  //   path: 'water-safety-IQ',
  //   canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
  //   canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
  //   loadChildren: () => import('./wsi-task-manager/wsi-task-manager.module').then(m => m.WsiTaskManagerModule),
  //   data: {
  //     breadcrumb: 'Enterprise Overview',
  //     roles: [USERROLES.WSAFTYIQTM],
  //     serviceTypeId: ServiceType.WaterManagementPlan,
  //     hideSubHeader: true,
  //   },
  // },
  /******** Coolant Health IQ Route starts***********/
  {
    path: 'coolant-health-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('../libraries/e3dindustrial-coolant-health-iq/libs/coolant-health-iq/src/lib/coolant-health-iq.routes').then(m => m.COOLANT_HEALTH_IQ_ROUTES),
    data: {
      breadcrumb: 'Coolant Health IQ',
      hideSubHeader: true,
      roles: [USERROLES.COOLANT_HEALTH_IQ]
    },
  },
  /******** Coolant Health IQ Route ends***********/
  /********Water Quality Route starts***********/
  {
    matcher: qualityRedirectMatcher,
    canActivate: [QualityRedirectGuard],
    component: ChildRouteRedirectComponent
  },
  {
    path: 'water-quality-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./quality/quality.module').then(m => m.QualityModule),
    data: {
      breadcrumb: 'Ecolab® Water Quality IQ™',
      roles: [USERROLES.WQDASHBOARD],
      serviceTypeId: ServiceType.WaterQuality
    },
  },
  /********Water Quality Route ends***********/

  // asset details.
  {
    path: 'asset-details',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./asset-details/asset-details.module').then(m => m.AssetDetailsModule),
    data: {
      breadcrumb: 'Asset Details',
      path: 'asset-details',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  //////equipment manager
  {
    path: 'equipment-manager',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./equipment-manager/equipment-manager.module').then(m => m.EquipmentManagerModule),
    data: {
      breadcrumb: 'Equipment-Manager',
      path: 'equipment-manager',
      roles: [USERROLES.ASSETHARVESTERDECOMMISSIONWRITE],
    },
  },
  // linked omni module.
  {
    path: 'omni-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./omni-iq/omni.module').then(m => m.OmniModule),
    data: {
      breadcrumb: 'OMNI',
      path: 'omni-iq',
      roles: [USERROLES.OMNIDASHBOARD /*USERROLES.OMNIDASHBOARD*/],
    },
  },

  {
    path: 'condenseriq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./condenser-iq/condenser.module').then(m => m.CondenserModule),
    data: {
      breadcrumb: 'Ecolab® CondenserIQ™',
      path: 'condenseriq',
      roles: [USERROLES.OMNIDASHBOARD],
      condenserPreselect: true
    },
  },
  {
    path: 'air-separation-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./air-separation-iq/air-separation.module').then(m => m.AirSeparationModule),
    data: {
      breadcrumb: 'Ecolab® Air Separation IQ™',
      path: 'air-separation-iq',
      roles: [USERROLES.OMNIDASHBOARD],
      airSeparationPreselect: true
    },
  },
  {
    path: 'chiller-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('../libraries/e3dindustrial-chiller-iq-mfe/libs/chiller-iq/src/public-api').then(m => m.CHILLER_IQ_ROUTES),
    data: {
      breadcrumb: 'Ecolab® ChillerIQ™',
      path: 'chiller-iq',
      hideSubHeader: true,
      roles: [USERROLES.CHILLER_IQ_DASHBOARD, USERROLES.CHILLER_IQ_ADMIN],
      chillerPreselect: true
    },
  },
  {
    path: 'heat-exchanger-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./heat-exchanger-iq/heat-exchanger.module').then(m => m.HeatExchangerModule),
    data: {
      breadcrumb: 'Ecolab® Heat Exchanger IQ™',
      path: 'heat-exchanger-iq',
      roles: [USERROLES.OMNIDASHBOARD],
      heatExchangerPreselect: true
    },
  },
  {
    path: 'rolling-mills-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./rolling-mills-iq/rolling-mills-iq.module').then(m => m.RollingMillsIqModule),
    data: {
      breadcrumb: 'Ecolab® Rolling Mills IQ™',
      path: 'rolling-mills-iq',
      roles: [USERROLES.OMNIDASHBOARD],
      rollingMillsPreselect: true
    }
  },
  {
    path: 'caster-systems-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./caster-systems-iq/caster-systems-iq.module').then(m => m.CasterSystemsIqModule),
    data: {
      breadcrumb: 'Ecolab® Caster Systems IQ™',
      path: 'caster-systems-iq',
      roles: [USERROLES.OMNIDASHBOARD],
      casterSystemsPreselect: true
    }
  },
  {
    path: 'ro-optimizer',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./ro-optimizer/ro-optimizer.module').then(m => m.RoOptimizerModule),
    data: {
      breadcrumb: 'RO Optimizer',
      roles: [USERROLES.RODASHBOARD],
    },
  },
  {
    path: 'recycle-intelligence',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./recycle-intelligence/recycle-intelligence.module').then(m => m.RecycleIntelligenceModule),
    data: {
      breadcrumb: "Recycle Intelligence",
      path: 'recycle-intelligence',
      roles: [USERROLES.RECYCLE_INTELLIGENCE]
    }
  },
  {
    path: 'ecolab-bi-report',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./ecolab-bi-report/ecolab-bi-report.module').then(m => m.EcolabBiReportModule),
    data: {
      breadcrumb: "我的自定义看板",
      path: 'ecolab-bi-report',
      roles: [USERROLES.RECYCLE_INTELLIGENCE]
    }
  },
  {
    path: 'recycle-intelligence-tcd',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./recycle-intelligence-tcd/recycle-intelligence-tcd.module').then(m => m.RecycleIntelligenceTcdModule),
    data: {
      breadcrumb: "TCD Smart RO Dashboard",
      path: 'recycle-intelligence-tcd',
      roles: [USERROLES.RECYCLE_INTELLIGENCE]
    }
  },
  {
    path: 'recycle-intelligence-etrip',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./recycle-intelligence-etrip/recycle-intelligence-etrip.module').then(m => m.RecycleIntelligenceEtripModule),
    data: {
      breadcrumb: "E-Trip",
      path: 'recycle-intelligence-etrip',
      roles: [USERROLES.RECYCLE_INTELLIGENCE]
    }
  },
  {
    path: 'recycle-intelligence-smart-service',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./recycle-intelligence-smart-service/recycle-intelligence-smart-service.module').then(m => m.RecycleIntelligenceSmartServiceModule),
    data: {
      breadcrumb: "Smart Service",
      path: 'recycle-intelligence-smart-service',
      roles: [USERROLES.RECYCLE_INTELLIGENCE]
    }
  },
  {
    path: 'digital-intelligence',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./digital-intelligence/digital-intelligence.module').then(m => m.DigitalIntelligenceModule),
    data: {
      breadcrumb: "Digital Intelligence",
      path: 'digital-intelligence',
      hideSubHeader: true,
      roles: [USERROLES.RECYCLE_INTELLIGENCE]
    }
  },
  {
    path: 'climate-intelligence',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./climate-intelligence/enterprise-overview/enterprise-overview.module').then(m => m.EnterpriseOverviewModule),
    data: {
      breadcrumb: "Climate-Intelligence",
      path: 'climate-intelligence',
      roles: [USERROLES.CLIMATE_INTELLIGENCE]
    }
  },
  {
    matcher: feedflexRedirectMatcher,
    canActivate: [FeedFlexRedirectGuard],
    component: ChildRouteRedirectComponent
  },
  {
    path: 'feed-flex-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./crude-flex/crude-flex.module').then(m => m.CrudeFlexModule),
    data: {
      breadcrumb: "Ecolab® Feed Flex IQ™",
      path: 'feed-flex-iq',
      roles: [USERROLES.FEED_FLEX,
      USERROLES.FFRSACALC,
      USERROLES.ADDCRD,
      ],
      hideSubHeader: true
    }
  },
  {
    path: 'inventory',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./inventory/inventory.module').then(m => m.InventoryModule),
    data: {
      breadcrumb: "Inventory",
      path: 'inventory',
      roles: [USERROLES.INVENTORY]
    }
  },
  {
    path: 'climate-intelligence/admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./climate-intelligence/admin/admin.module').then(m => m.AdminModule),
    //  component: FinancialImpactAdminComponent,
    //  pathMatch: 'prefix',
    data: {
      breadcrumb: 'Climate-Intelligence Admin',
      path: 'climate-intelligence/admin',
      roles: [USERROLES.CLIMATE_INTELLIGENCE_ADMIN /*USERROLES.CIADMIN*/],
    },
  },
  {
    path: 'thermotwin-iq/admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./climate-intelligence-twin-iq/admin/admin.module').then(m => m.AdminModule),
    //  component: FinancialImpactAdminComponent,
    //  pathMatch: 'prefix',
    data: {
      breadcrumb: 'Climate-Intelligence-ThermoTwinIQ Admin',
      path: 'thermotwin-iq/admin',
      roles: [USERROLES.THERMO_TWIN_IQ_ADMIN /*USERROLES.CIADMIN*/],
    },
  },
  {
    path: 'utility-sense-iq/admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./utility-sense-iq/admin/admin.module').then(m => m.AdminModule),
    data: {
      breadcrumb: 'Utility Sense IQ Admin',
      path: 'utility-sense-iq/admin',
      roles: [USERROLES.UTILITY_SENSE_IQ_ADMIN],
    },
  },
  {
    path: 'system-assurance/admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./system-assurance-admin/system-assurance-admin.module').then(m => m.SystemAssuranceAdminModule),
    data: {
      breadcrumb: 'System Assurance',
      roles: [USERROLES.SAADMIN],
    },
  },
  {
    path: 'omni/admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./omni/admin/admin.module').then(m => m.AdminModule),
    //  component: FinancialImpactAdminComponent,
    //  pathMatch: 'prefix',
    data: {
      breadcrumb: 'Omni Admin',
      path: 'omni/admin',
      roles: [USERROLES.OMNIADMIN /*USERROLES.FIADMIN*/],
    },
  },
  {
    path: 'financial-impact',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./financial-impact/financial-impact.module').then(m => m.FinancialImpactModule),
    data: {
      breadcrumb: 'Financial Impact',
      path: 'financial-impact',
      roles: [USERROLES.FIDASHBOARD],
    },
  },
  {
    path: 'financial-impact/admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./financial-impact/admin/admin.module').then(m => m.AdminModule),
    //  component: FinancialImpactAdminComponent,
    //  pathMatch: 'prefix',
    data: {
      breadcrumb: 'Financial Impact Admin',
      path: 'financial-impact/admin',
      roles: [USERROLES.FIADMIN],
    },
  },
  {
    path: 'financial-impact/ebr-drill',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadComponent:
      () => import('./shared-financial-impact-refresh/components/redirect-handler/redirect-handler.component').then(m => m.RedirectHandlerComponent),
    data: {
      hideSubHeader: true,
      roles: [],
    },
  },
  {
    path: 'data-visualization-old',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./data-visualization/data-visualization.module').then(m => m.DataVisualizationModule),
    // data: {
    //   breadcrumb: 'Data Visualization-old',
    //   path: 'data-visualization',
    //   roles: [USERROLES.VISDASHBOARD]
    // }
  },
  {
    path: 'data-visualization',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./data-visualization-library/data-visualization-library.module').then(m => m.DataVisualizationLibraryModule),
    data: {
      breadcrumb: 'Data Visualization',
      path: 'data-visualization',
      roles: [USERROLES.VISDASHBOARD],
    },
  },
  {
    path: 'data-visualization-v2',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('../libraries/e3duiplatform-data-visualization-mfe/libs/data-visualization/src/public-api').then(m => m.DATA_VISUALIZATION_V2_ROUTES),
    data: {
      breadcrumb: 'Data Visualization',
      path: 'data-visualization-v2',
      roles: [USERROLES.VISDASHBOARD],
      hideSubHeader: true,
    },
  },
  {
    path: 'data-viz-plot-chart',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./data-viz-plot-chart/data-viz-plot-chart.module').then(m => m.DataVizPlotChartModule),
    data: {
      breadcrumb: 'Data Visualization',
      path: 'data-viz-plot-chart',
      roles: [USERROLES.VISDASHBOARD],
    },
  },

  /******** Water Flow intelligence Route starts***********/
  {
    matcher: wfiRedirectMatcher,
    canActivate: [wfiRedirectGuard],
    component: ChildRouteRedirectComponent
  },
  {
    path: 'water-track-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./water-flow-intelligence/water-flow-intelligence.module').then(m => m.WaterFlowIntelligenceModule),
    data: {
      breadcrumb: 'Ecolab® Water Track IQ™',
      roles: [USERROLES.WFIDASHBOARD],
      serviceTypeId: ServiceType.WaterFlowIntelligence,
      hideSubHeader: true,
    },
  },
  /******** Water Flow intelligence Route ends***********/
  {
    path: 'water-flow-intelligence',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./water-flow-intelligence/water-flow-intelligence.module').then(m => m.WaterFlowIntelligenceModule),
    data: {
      breadcrumb: 'Water Flow Intelligence',
      path: 'water-flow-intelligence',
      roles: [USERROLES.WFIDASHBOARD],
      serviceTypeId: ServiceType.WaterFlowIntelligence,
      hideSubHeader: true,
    },
  },
  {
    path: 'water-flow-intelligence/admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./water-flow-intelligence/admin/admin.module').then(m => m.AdminModule),
    data: {
      breadcrumb: 'Water flow Intelligence Admin',
      path: 'water-flow-intelligence/admin',
      roles: [USERROLES.WFISENSORMAPPING],
    },
  },
  {
    path: 'notifications-admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./notifications-admin/notifications-admin.module').then(m => m.NotificationsAdminModule),
    data: {
      breadcrumb: 'Notifications Admin',
      path: 'notifications-admin',
      roles: [
        USERROLES.NOTIFICATIONS_ADMIN_INTERNAL,
        USERROLES.NOTIFICATIONS_ADMIN_OWN,
        USERROLES.NOTIFICATIONS_CUSTOMER_FULL,
        USERROLES.NOTIFICATIONS_CUSTOMER_ADD,
      ],
    },
  },
  {
    path: 'controller-credentials',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./controller-credentials/controller-credentials.module').then(m => m.ControllerCredentialsModule),
    data: {
      breadcrumb: 'Controller Credentials',
      path: 'controller-credentials',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    path: 'action-log',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./action-log/action-log.module').then(m => m.ActionLogModule),
    data: {
      breadcrumb: 'Action and Recommendations',
      path: 'action-log',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  // Equipment Hub Page
  {
    path: 'equipment-hub',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./equipment-hub/equipment-hub.module').then(m => m.EquipmentHubModule),
    data: {
      breadcrumb: 'Equipment Hub',
      path: 'equipment-hub',
      roles: [USERROLES.ASSETHARVESTERREADWRITE, USERROLES.ENVISIONSALES],
      hideSubHeader: true,
    },
  },

  // New UX Live Reading Page
  {
    path: 'NGC-configurator/:controllerId',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./nalco-configurator/ngc-configurator.module').then(m => m.NgcConfiguratorModule),
    data: {
      breadcrumb: 'NGC Configurator',
      path: 'NGC-configurator/:controllerId',
      roles: [USERROLES.ENVISION4WEB],
      hideSubHeader: true,
    },
  },


  // New UX Action and Recommendations Page
  {
    path: 'action-recommendations',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./actions-recommendations/actions-recommendations.module').then(m => m.ActionsRecommendationsModule),
    data: {
      breadcrumb: 'Action & Recommendations',
      path: 'action-recommendations',
      roles: [USERROLES.ENVISION4WEB],
      hideSubHeader: true,
    },
  },
  {
    path: 'my-documents',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./my-documents/my-documents.module').then(m => m.MyDocumentsModule),
    data: {
      breadcrumb: 'My Documents',
      path: 'my-documents',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    path: 'digital-twin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./digital-twin/digital-twin.module').then(m => m.DigitalTwinModule),
    data: {
      hideLayout: true,
      // roles: [USERROLES.WQDASHBOARD,USERROLES.OMNIDASHBOARD]
    },
  },
  {
    // eslint-disable-next-line max-len
    path: 'navigate-data-viz',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./navigate-data-viz/navigate-data-viz.module').then(m => m.NavigateDataVizModule),
    data: {
      breadcrumb: 'Data Visualization',
      path: 'navigate-data-viz',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    // eslint-disable-next-line max-len
    path: 'service-summary',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren:
      () => import('./service-summary/service-summary.module').then(m => m.ServiceSummaryModule),
    data: {
      breadcrumb: 'service-summary',
      path: 'service-summary',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    // eslint-disable-next-line max-len
    path: 'fnb',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    data: {
      breadcrumb: 'F & B',
      path: 'fnb',
      roles: [
        USERROLES.ASSETTREEADMIN,
        USERROLES.ASSETHARVESTERREAD,
        USERROLES.ASSETHARVESTERREADWRITE,
      ],
    },
    component: FnbComponent,
  },
  {
    path: 'common-pages',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./common-pages/common-pages.module').then(m => m.CommonPagesModule),
    data: {
      breadcrumb: 'Common Pages',
      path: 'common-pages',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    path: 'scale-guard-index',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./scale-guard-index/sgi.module').then(m => m.ScaleGuardIndexModule),
    data: {
      breadcrumb: 'Scale Guard Index',
      path: 'scale-guard-index',
      roles: [USERROLES.SGI_ADMIN_ACCESS, USERROLES.SGI_READ_ACCESS],
    },
  },
  {
    path: 'water-balance-intelligence',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./water-balance-intelligence/wbi.module').then(m => m.WBIModule),
    data: {
      breadcrumb: 'Water Balance Intelligence',
      path: 'water-balance-intelligence',
      roles: [USERROLES.WBI_READ_ACCESS],
    },
  },
  {
    path: 'ssd-analyzer',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./ssd-analyzer/ssd-analyzer.module').then(m => m.SsdAnalyzerModule),
    data: {
      breadcrumb: 'SSD Analyzer',
      path: 'ssd-analyzer',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    path: 'e-data-entry',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./e-data-entry/e-data-entry.module').then(m => m.EDATAModule),
    data: {
      breadcrumb: 'E Data',
      path: 'e-data-entry',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  /********FSQS Route starts***********/
  {
    matcher: fsqsRedirectMatcher,
    canActivate: [FsqsRedirectGuard],
    component: ChildRouteRedirectComponent
  },
  {
    path: 'food-safety-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./fsqs/fsqs.module').then(m => m.FsqsModule),
    data: {
      preload: true,
      roles: [USERROLES.FSQS_PROCEDURE_WRITE,
      USERROLES.FSQS_PROCEDURE_READ,
      USERROLES.FSQS_AH_LOCATION_READ,
      USERROLES.FSQS_AH_LOCATION_WRITE,
      USERROLES.FSQS_PROCEDURE_LOG_READ,
      USERROLES.FSQS_PROCEDURE_LOG_WRITE,
      USERROLES.FSQS_EMPSERVICE_DASHBOARD,
      USERROLES.FSQS_PTSSERVICE_DASHBOARD,
      USERROLES.SERVICE_PLAN_LOG_READ,
      USERROLES.SERVICE_PLAN_LOG_WRITE,
      USERROLES.SERVICE_PLAN_READ,
      USERROLES.SERVICE_PLAN_WRITE
      ],
    },
  },
  {
    path: 'admin-alarm',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./admin-alarm/admin-alarm.module').then(m => m.AdminAlarmModule),
    data: {
      path: 'admin-alarm',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    path: 'alarm-user-mgmt',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./alarm-user-mgmt/alarm-user-mgmt.module').then(m => m.AlarmUserMgmtModule),
    data: {
      path: 'alarm-user-mgmt',
      roles: [USERROLES.ENVISION4WEB],
    },
  },

  /********WFI Alarms Route starts***********/
  {
    path: 'WFI-alarm',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./wfi-alarm/wfi-alarm.module').then(m => m.WfiAlarmModule),
    data: {
      path: 'WFI-alarm',
      roles: [USERROLES.ENVISION4WEB],
    },
  },
  {
    path: 'water-track-iq-alarm',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./wfi-alarm/wfi-alarm.module').then(m => m.WfiAlarmModule),
    data: {
      path: 'water-track-iq-alarm',
      roles: [USERROLES.ENVISION4WEB],
    },
  },

  /********WFI Alarms Route ends***********/
  {
    path: 'connectivity',
    canActivate: [AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./connectivity/connectivity.module').then(m => m.ConnectivityModule),
    data: {
      breadcrumb: "Connectivity",
      path: 'connectivity',
      roles: [USERROLES.IOTCWP] ////////need change
    }
  },
  {
    path: 'my-territory',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./my-territory/my-territory.module').then(m => m.MyTerritoryModule),
    data: {
      breadcrumb: 'My Territory',
      hideSubHeader: true,
      path: 'my-territory',
      roles: [USERROLES.MTV_ACCESS],
    },
  },
  {
    path: 'operational-reliability-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./total-booth-management/total-booth-management.module').then(m => m.TBMModule),
    data: {
      breadcrumb: " Ecolab® Operational Reliability IQ™",
      path: 'operational-reliability-iq',
      roles: [USERROLES.TOTAL_BOOTH_MANAGEMENT]
    }
  },
  {
    matcher: paperRedirectMatcher,
    canActivate: [PaperRedirectGuard],
    component: ChildRouteRedirectComponent
  },
  {
    path: 'pulp-&-paper-intelligence',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./paper/paper.module').then(m => m.PaperModule),
    data: {
      breadcrumb: "Pulp & Paper Intelligence",
      path: 'pulp-&-paper-intelligence',
      roles: [USERROLES.PAPER,
      USERROLES.Paper_YOI,
      USERROLES.Paper_PWI,
      USERROLES.Paper_RBLI,
      USERROLES.MAIADMIN,
      ]
    }
  },
  {
    path: 'thermotwin-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./climate-intelligence-twin-iq/climate-intelligence-twin-iq.module').then(m => m.ClimateIntelligenceTwinIqModule),
    data: {
      breadcrumb: "ThermoTwin IQ",
      path: 'thermotwin-iq',
      roles: [USERROLES.THERMO_TWIN_IQ]
    }
  },
  {
    path: 'utility-sense-iq',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./utility-sense-iq/utility-sense-iq.module').then(m => m.UtilitySenseIqModule),
    data: {
      breadcrumb: "Utility Sense IQ",
      path: 'utility-sense-iq',
      roles: [USERROLES.UTILITY_SENSE_IQ]
    }
  },
  {
    path: 'manual-equipment-creation',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./manual-equipment-creation/manual-equipment-creation.module').then(m => m.ManualEquipmentCreationModule),
    data: {
      breadcrumb: 'Manual Asset Creation',
      globalIdleEnabled: false
    }
  },
  {
    path: 'water-for-climate',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./water-for-climate/water-for-climate.module').then(m => m.WaterForClimateModule),
    data: {
      breadcrumb: 'Water for climate',
      path: 'water-for-climate',
      roles: ["116"],
    }
  },
  {
    path: 'create-new-template',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: () => import('./ssd-template-builder/template-builder.module').then(m => m.TemplateBuilderModule),
    data: {
      breadcrumb: '',
      path: 'create-new-template',
      roles: [USERROLES.SSD_TB_Admin],
    },
  },
  /* Commenting as this feature is on hold
  {
    path: 'platform-notifications-admin',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    canActivateChild: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
    loadChildren: ()=> import('./platform-notifications-admin/platform-notifications-admin.module').then(m=>m.PlatformNotificationsAdminModule),
    data: {
      breadcrumb: 'Platform Notifications Admin',
      path: 'platform-notifications-admin',
      roles: [USERROLES.PFMNFTADMN],
    },
  },
  */
  {
    path: 'consent-page',
    component: ConsentPageComponent,
    pathMatch: 'prefix',
  },
  {
    path: 'downtime',
    component: ApplicationDowntimeComponent,
    pathMatch: 'prefix',
  },
  {
    path: 'error-page',
    component: ErrorPageComponent,
    pathMatch: 'prefix',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard]

  },
  {
    path: '**',
    component: ErrorPageComponent,
    pathMatch: 'prefix',
    canActivate: [MsalGuard, AuthenticationGuard, AuthorizationGuard],
  }

];

// eslint-disable-next-line
export const APP_ROUTER: ModuleWithProviders<any> = RouterModule.forRoot(
  APP_ROUTES,
  {
    preloadingStrategy: SelectivePreloadingStrategy,
    useHash: true,
    // enableTracing: true,
    // Don't perform initial navigation in iframes or popups
    paramsInheritanceStrategy: 'always',
    initialNavigation:
      !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
        ? 'enabledNonBlocking'
        : 'disabled', // Set to enabledBlocking to use Angular Universal
  })


/*
export class AppRoutingModule {
  vpView: any;

  // Subscribe to the Router URL changes and report the metrics to AppDynamics.
  constructor(public router: Router) {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        console.debug('NavigationEnd: ' + event.url);

        this.vpView.markViewChangeEnd();
        this.vpView.markViewDOMLoaded();
        this.vpView.markXhrRequestsCompleted();
        this.vpView.markViewResourcesLoaded();
        this.vpView.end();
        ADRUM.report(this.vpView);

      } else if (event instanceof NavigationStart) {
        console.debug('NavigationStart: ' + event.url);

        this.vpView = new ADRUM.events.VPageView();
        this.vpView.start();
        this.vpView.markViewChangeStart();
      }
    });
  };
}
*/
